import React from "react"
import { View, ScrollView, TouchableOpacity } from "react-native"
import DatePickerModalM from "@common/components/modal/DatePickerModalM"
import { theme } from "@common/theme/theme"
import TextM from "@common/components/text/TextM"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { IconImageM } from "@common/components/icons/IconsM"
import ChapterAutocomplete from "../student/schoolAssessments/ChapterAutocomplete"
import UncontrolledTextInputM from "@common/components/input/UncontrolledTextInputM"
import moment from "moment"

const ManualFormSection = ({
    filteredSubjects,
    selectedSubjectId,
    onSubjectSelect,
    watchedDate,
    onDateChange,
    datePickerVisible,
    setDatePickerVisible,
    availableChapters,
    selectedChapterIds,
    onToggleChapter,
    customChapters,
    onAddCustomChapter,
    onRemoveCustomChapter,
    keyRequirementsText,
    onKeyRequirementsChange,
    chaptersLoading,
    loading,
}) => {
    const hasChaptersSelected =
        selectedChapterIds.length > 0 || customChapters.length > 0

    return (
        <View>
            {/* Subject selection */}
            <TextM
                fontSize={14}
                fontWeight="Bold"
                color={theme.colors.primary}
                style={{ marginBottom: 12 }}
            >
                C'est pour quelle matière ?
            </TextM>
            <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                style={{ marginBottom: 24 }}
            >
                {filteredSubjects.map((subject) => {
                    const isSelected = selectedSubjectId === subject.id
                    const subjectColor = subject.color || theme.colors.primary
                    return (
                        <TouchableOpacity
                            key={subject.id}
                            onPress={() => onSubjectSelect(subject.id)}
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                paddingHorizontal: 14,
                                paddingVertical: 10,
                                borderRadius: 20,
                                marginRight: 10,
                                backgroundColor: isSelected
                                    ? subjectColor
                                    : theme.colors.white,
                                borderWidth: 2,
                                borderColor: isSelected
                                    ? subjectColor
                                    : theme.colors.lightMore,
                            }}
                        >
                            {subject.icon && (
                                <IconImageM
                                    uri={subject.icon}
                                    width={22}
                                    style={{ marginRight: 8 }}
                                />
                            )}
                            <TextM
                                fontSize={14}
                                fontWeight={isSelected ? "Bold" : "SemiBold"}
                                color={
                                    isSelected
                                        ? theme.colors.white
                                        : theme.colors.grey
                                }
                            >
                                {subject.name}
                            </TextM>
                        </TouchableOpacity>
                    )
                })}
            </ScrollView>

            {/* Date picker */}
            <TextM
                fontSize={14}
                fontWeight="Bold"
                color={theme.colors.primary}
                style={{ marginBottom: 12 }}
            >
                C'est pour quand ?
            </TextM>
            <TouchableOpacity
                onPress={() => setDatePickerVisible(true)}
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    padding: 16,
                    borderRadius: 12,
                    borderWidth: 2,
                    borderColor: theme.colors.lightMore,
                    backgroundColor: theme.colors.white,
                    marginBottom: 24,
                }}
            >
                <View
                    style={{
                        width: 36,
                        height: 36,
                        borderRadius: 18,
                        backgroundColor: theme.colors.primaryLighter,
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: 12,
                    }}
                >
                    <FontAwesome5
                        name="calendar-alt"
                        size={16}
                        color={theme.colors.primary}
                    />
                </View>
                <TextM fontSize={15} fontWeight="SemiBold">
                    {moment(watchedDate).format("dddd D MMMM YYYY")}
                </TextM>
            </TouchableOpacity>

            <DatePickerModalM
                visible={datePickerVisible}
                onDismiss={() => setDatePickerVisible(false)}
                value={watchedDate}
                onConfirm={(date) => {
                    setDatePickerVisible(false)
                    onDateChange(date)
                }}
                validRange={{ startDate: new Date() }}
                label="Date du contrôle"
                allowEditing
                applyOnSaveOnly
            />

            {/* Chapters selection */}
            {selectedSubjectId && (
                <>
                    <TextM
                        fontSize={14}
                        fontWeight="Bold"
                        color={theme.colors.primary}
                        style={{ marginBottom: 12 }}
                    >
                        Sur quels chapitres ?
                    </TextM>

                    {/* Custom chapters chips */}
                    {customChapters.length > 0 && (
                        <View
                            style={{
                                flexDirection: "row",
                                flexWrap: "wrap",
                                marginBottom: 12,
                            }}
                        >
                            {customChapters.map((chapterName) => (
                                <TouchableOpacity
                                    key={chapterName}
                                    onPress={() =>
                                        onRemoveCustomChapter(chapterName)
                                    }
                                    style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        backgroundColor:
                                            theme.colors.warningLight,
                                        borderRadius: 16,
                                        paddingHorizontal: 12,
                                        paddingVertical: 6,
                                        marginRight: 8,
                                        marginBottom: 8,
                                        borderWidth: 1,
                                        borderColor: theme.colors.warning,
                                    }}
                                >
                                    <TextM
                                        fontSize={12}
                                        fontWeight="SemiBold"
                                        color={theme.colors.warningDark}
                                        numberOfLines={1}
                                        style={{ maxWidth: 150 }}
                                    >
                                        {chapterName}
                                    </TextM>
                                    <FontAwesome5
                                        name="times"
                                        size={12}
                                        color={theme.colors.warningDark}
                                        style={{ marginLeft: 6 }}
                                    />
                                </TouchableOpacity>
                            ))}
                        </View>
                    )}

                    <ChapterAutocomplete
                        chapters={availableChapters}
                        selectedChapterIds={selectedChapterIds}
                        onToggleChapter={onToggleChapter}
                        onAddCustomChapter={onAddCustomChapter}
                        loading={chaptersLoading}
                        disabled={loading}
                    />
                </>
            )}

            {/* Key requirements section - only shown when chapters are selected */}
            {hasChaptersSelected && (
                <>
                    <TextM
                        fontSize={14}
                        fontWeight="Bold"
                        color={theme.colors.primary}
                        style={{ marginBottom: 12, marginTop: 24 }}
                    >
                        Détails
                    </TextM>
                    <UncontrolledTextInputM
                        value={keyRequirementsText}
                        onChangeText={onKeyRequirementsChange}
                        placeholder="Ex: ce qu'il faut connaître, savoir-faire..."
                        multiline
                        numberOfLines={4}
                        style={{
                            minHeight: 100,
                        }}
                        contentContainerStyle={{ marginBottom: 0 }}
                    />
                </>
            )}
        </View>
    )
}

export default ManualFormSection
